import React, { useState } from 'react';
import { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { analyticsLogger, PageType, ReportType } from 'commons/src/analytics';
import { INSIGHT_CREATED_REPORT } from 'commons/src/analytics/AnalyticsEvents';
import Error from 'commons/src/components/errorComponents/Error';
import PageHeader from 'commons/src/components/headers/PageHeader';
import SubHeader from 'commons/src/components/headers/SubHeader';
import { IntercomAPI } from 'commons/src/components/Intercom';
import { mediumFormLoader } from 'commons/src/components/placeholder';
import ResponseBox from 'commons/src/components/responseMessages/ResponseBox';
import { radonInsightReportDevices } from 'commons/src/DeviceAndSensorLists';
import { ErrorType } from 'commons/src/models/commonTypeScript';
import { fetchRadonInsightData } from '../../../actions/insightActions';
import { paths } from '../../../constants';
import { BuildingProps } from '../../../models/commonEnums';
import { Store } from '../../../reducers';
import { BusinessRequestType as RequestType } from '../../../reducers/BusinessRequestType';
import InsightMultipleDevices, { ReportData } from '../reportSelector/InsightMultipleDevices';
import InsightResultView from './InsightResultView';

export const RadonInsight = (): React.ReactElement => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t: txt } = useTranslation();

    const locationsLoading = useSelector(
        (state: Store) => state.locations.loading && state.locations.locations.length === 0
    );
    const locationsError = useSelector((state: Store) => state.locations.error);
    const fetchReportDataLoading = useSelector(
        (state: Store) => state.requests[RequestType.FetchRadonInsightData]?.loading || false
    );
    const error = useSelector((state: Store) => state.requests[RequestType.FetchRadonInsightData]?.error) as
        | ErrorType
        | undefined;

    const [reportStartTime, setReportStartTime] = useState<Moment | null>(null);
    const [reportEndTime, setReportEndTime] = useState<Moment | null>(null);
    const [displayResultSection, setDisplayResultSection] = useState(false);
    const optionalBuildingProps = [BuildingProps.floors, BuildingProps.buildingYear];
    if (locationsError) {
        return <Error />;
    }

    const fetchReportData = (reportData: ReportData): void => {
        const { serialNumbers, from, to, locationId, includeLogo } = reportData;
        analyticsLogger(INSIGHT_CREATED_REPORT, {
            pageType: PageType.Insight,
            reportType: ReportType.Radon,
            reportData,
        });

        IntercomAPI('trackEvent', 'generated-radon-insight-report', {
            serialNumbers,
            from,
            to,
        });
        dispatch(
            fetchRadonInsightData({
                from,
                to,
                serialNumbers,
                locationId,
                includeLogo: includeLogo || false,
            })
        );
    };

    const goBack = (): void => {
        navigate({ pathname: `/${paths.reports}` });
    };
    return (
        <div className="radon-insight-view">
            <PageHeader title={txt('RadonInsight.RadonMitigation')} />
            <div className="page-wrapper-flex">
                <SubHeader onGoBack={goBack} backTitle="Reports.Reports" actionButtons={[]} />
            </div>
            <div className="page-wrapper-flex page-wrapper__medium--white">
                <ReactPlaceholder ready={!locationsLoading} customPlaceholder={mediumFormLoader}>
                    <div className="form form__wide-container">
                        <div className="header-2">
                            <span className="text-large">{txt('RadonInsight.RadonMitigationDescription')}</span>
                        </div>
                        <InsightMultipleDevices
                            setDisplayResultSection={setDisplayResultSection}
                            fetchReportDataLoading={fetchReportDataLoading}
                            fetchReportData={fetchReportData}
                            reportStartTime={reportStartTime}
                            reportEndTime={reportEndTime}
                            setReportStartTime={setReportStartTime}
                            setReportEndTime={setReportEndTime}
                            acceptedDeviceTypes={radonInsightReportDevices}
                            minNumberOfDays={32}
                            allowLogo
                            optionalBuildingProps={optionalBuildingProps}
                            hideSegmentProperties
                        />
                        {error && <ResponseBox text={`ErrorCodes.${error.error}`} />}
                    </div>
                </ReactPlaceholder>
            </div>
            {displayResultSection && !error && (
                <InsightResultView
                    from={reportStartTime}
                    to={reportEndTime}
                    optionalBuildingProps={optionalBuildingProps}
                />
            )}
        </div>
    );
};

export default RadonInsight;
